import { useQuery } from '@tanstack/react-query'
import cn from 'classnames'
import AmountInput from 'ext-ui/components/AmountInput/AmountInput'
import BaseFlyout from 'ext-ui/components/BaseFlyout/BaseFlyout'
import Button from 'ext-ui/components/Button/Button'
import TextAreaInput from 'ext-ui/components/TextAreaInput/TextAreaInput'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import { Moment } from 'moment-timezone'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useDispatch } from 'react-redux'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import DateInput from 'src/component-form/DateInput/DateInput'
import MfaStepUpModal from 'src/component-modal/MfaStepUpModal/MfaStepUpModal'
import SvgChevronRight from 'src/components/Svgs/SvgChevronRight'
import useConfig from 'src/context/ConfigContext/useConfig'
import { ExtendCurrency } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { queryCacheOptions } from 'src/queries/queries.constants'
import { vendorQueries } from 'src/queries/vendors/vendors.queries'
import { ListVendorsParams, Vendor } from 'src/queries/vendors/vendors.types'

import InvoicePreviewShelf from './InvoicePreviewShelf/InvoicePreviewShelf'
import InvoiceUploader from './InvoiceUploader/InvoiceUploader'
import style, {
  datesRow,
  fields,
  flyoutTitle,
  footer,
  footerButtons,
  header,
  headerDividerFullBleed,
  memoField,
  section,
  sectionDivider,
  sectionFields,
  sectionTitle,
  stepBody,
} from './PayBillFlyout.style'
import ReviewStep from './ReviewStep/ReviewStep'
import useBillPaymentFlow from './useBillPaymentFlow'
import { isPayableVendor } from './VendorSelect/useVendorSelectOptions'
import VendorSelect from './VendorSelect/VendorSelect'

export interface PayBillFlyoutProps {
  isOpen: boolean
  onClose: () => void
  organizationId: string
  viewerUserId?: string
  className?: string
}

type Step = 'details' | 'review'

/**
 * Pathological-input clamp only — NOT a business rule. `AmountInput` silently
 * clamps to `maxValue`, which is the right behaviour for "someone pasted forty
 * digits" and the wrong behaviour for a real limit. House convention shared
 * with LowBalanceAlertField, ExpenseForm, VirtualCardForm, … The actual ceiling
 * is ACH_MAX_AMOUNT below, which is enforced as an ERROR rather than a clamp.
 */
const AMOUNT_MAX_VALUE = 99999999

/**
 * Largest amount Modern Treasury accepts on a single ACH payment order.
 *
 * Verified directly against MT's sandbox validator (2026-08-05), which rejects
 * a larger `amount` with `"must be less than or equal to 100000000"` — their
 * amount is in cents, so the ceiling is $1,000,000.00. It applies to
 * NORMAL-priority ACH too, not only Same Day: Nacha caps Same Day ACH per
 * payment (at $1,000,000 today, rising to $10,000,000 on 2027-09-17) but sets
 * no per-payment cap on standard ACH, so this one is MT/partner-bank policy
 * rather than a network rule.
 *
 * ⚠️ Two reasons this must not stay a frontend literal:
 *   1. Measured in SANDBOX. The live partner bank may cap lower.
 *   2. It moves in 2027, and possibly sooner.
 * Replace it with a backend-supplied limit once the create-payment endpoint
 * exists (EX-40563 / EX-40566) — there is no such endpoint today, so nothing
 * server-side can report this yet and an over-limit amount would otherwise be
 * discovered only at submit, a whole step away from the field that caused it.
 *
 * Enforced as a blocking validation message, never a clamp: $1,000,000 is
 * itself a legitimate amount, so silently rewriting 2,500,000 down to it would
 * hand the user a plausible, wrong number to submit.
 */
const ACH_MAX_AMOUNT = 1000000

/**
 * Display form of the limit, derived from the constant so the message and the
 * rule can never disagree. ACH is USD-only, but the number and symbol placement
 * still follow the active app locale. The locale is already available from
 * ConfigContext, so this keeps the flyout free of Redux dependencies.
 */
const formatAchMaxAmountLabel = (locale: string): string =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: ExtendCurrency.Usd,
    maximumFractionDigits: 0,
  }).format(ACH_MAX_AMOUNT)

/**
 * The only network call this flyout makes: reading the vendor list. Stable
 * module const so the query key is stable. A single page of 100 is plenty for
 * the happy path; the picker filters (payable) and searches (name/email)
 * client-side over it. Deeper pagination is a documented follow-up.
 */
const VENDOR_LIST_PARAMS: ListVendorsParams = { page: 0, count: 100 }

/** Stable reference for "no vendors yet" — see the `?? NO_VENDORS` note below. */
const NO_VENDORS: Vendor[] = []

const FLYOUT_TITLE_ID = 'pay-bill-flyout-title'

/* Each section is a landmark labelled by its own heading, so screen-reader
   users get the same three-part grouping the dividers convey visually. */
const VENDOR_SECTION_ID = 'pay-bill-section-vendor'
const ATTACHMENT_SECTION_ID = 'pay-bill-section-attachment'
const DETAILS_SECTION_ID = 'pay-bill-section-details'

/**
 * Pay Bill flyout, Step 1 (Details) and the transaction-bound authorization
 * step. The controller owns request identity, retries, reconciliation, and
 * attachment cleanup; this component only owns the draft UI and its lifecycle.
 */
const PayBillFlyout: React.FC<PayBillFlyoutProps> = ({
  isOpen,
  onClose,
  organizationId,
  viewerUserId,
  className,
}) => {
  const translate = useTranslate()
  const { locale } = useConfig()
  const dispatch = useDispatch()
  const flow = useBillPaymentFlow(organizationId, viewerUserId)
  const { reset: resetFlow, uploadInvoiceFile, discardUnattachedFile } = flow

  const [step, setStep] = useState<Step>('details')
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null)
  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [amount, setAmount] = useState<number | undefined>(undefined)
  const [invoiceDate, setInvoiceDate] = useState<Moment | undefined>(undefined)
  const [dueDate, setDueDate] = useState<Moment | undefined>(undefined)
  const [memo, setMemo] = useState('')
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null)
  const [isAttachingFile, setIsAttachingFile] = useState(false)
  const [fileError, setFileError] = useState<string | null>(null)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)

  const detailsRef = useRef<HTMLDivElement>(null)
  const reviewRef = useRef<HTMLDivElement>(null)
  const wasOpenRef = useRef(false)
  const organizationIdRef = useRef(organizationId)
  const draftSessionRef = useRef(0)
  const fileOperationRef = useRef(0)
  // Skip the focus move on the first render of a freshly-opened flyout —
  // BaseFlyout owns initial focus; we only move focus when the step CHANGES.
  const skipStepFocusRef = useRef(true)

  const vendorsQuery = useQuery({
    ...vendorQueries.list(organizationId, VENDOR_LIST_PARAMS),
    // The client sets no default staleTime, so without this the list refetches
    // every time the flyout opens (`enabled` flips false -> true on a stale
    // query). Vendor mutations already invalidate `vendorKeys.lists`, so a
    // cached list cannot go quietly out of date.
    ...queryCacheOptions(),
    // Preserve the factory's org-presence guard; only fetch while open.
    enabled: isOpen && Boolean(organizationId),
  })
  // Stable empty array: `?? []` would mint a new identity on every render while
  // the query has no data, invalidating the payable-filter memo downstream.
  const vendors = vendorsQuery.data?.vendors ?? NO_VENDORS

  // Reset on OPEN (not close), so the panel never flashes an empty form on the
  // way out — same rationale as AddVendorFlyout. Cancel discards without a
  // confirmation dialog (decision #4).
  useLayoutEffect(() => {
    const wasOpen = wasOpenRef.current
    const organizationChanged = organizationIdRef.current !== organizationId
    wasOpenRef.current = isOpen
    organizationIdRef.current = organizationId
    if (!isOpen) {
      if (wasOpen || organizationChanged) {
        // Now that the invoice uploads on attach rather than on confirm, a
        // draft abandoned here has already put a file on the server. Discard it
        // before bumping the session — `reset()` only drops the local handle to
        // the attempt, so anything not deleted now is orphaned for good. Fire
        // and forget: the flyout is on its way out and a failed cleanup has
        // nowhere left to report to.
        void discardUnattachedFile(null).catch(() => undefined)
        draftSessionRef.current += 1
        fileOperationRef.current += 1
      }
      skipStepFocusRef.current = true
      return
    }
    if (wasOpen && !organizationChanged) return
    // Same reason as the close branch: an org switch with the flyout still open
    // reaches `resetFlow()`, which drops the handle to an uploaded attachment
    // without deleting it.
    if (wasOpen && organizationChanged) {
      void discardUnattachedFile(null).catch(() => undefined)
    }
    draftSessionRef.current += 1
    fileOperationRef.current += 1
    resetFlow()
    setStep('details')
    setSelectedVendor(null)
    setInvoiceNumber('')
    setAmount(undefined)
    setInvoiceDate(undefined)
    setDueDate(undefined)
    setMemo('')
    setInvoiceFile(null)
    setIsAttachingFile(false)
    setFileError(null)
    setIsPreviewOpen(false)
    // `discardUnattachedFile` is deliberately not a dependency, and listing it
    // would change nothing: `resetFlow` is already in the array and is rebuilt
    // on every render (its own deps are React Query mutation results, which are
    // fresh objects each time), so this effect ALREADY runs every render. What
    // makes that safe is `wasOpenRef` — both branches read it and re-entry
    // short-circuits, so neither the reset nor the discard can fire twice.
  }, [isOpen, organizationId, resetFlow])

  // Move focus into the step's subtree when the step changes (accessibility).
  useEffect(() => {
    if (!isOpen) return
    if (skipStepFocusRef.current) {
      skipStepFocusRef.current = false
      return
    }
    if (step === 'review') reviewRef.current?.focus()
    else detailsRef.current?.focus()
  }, [step, isOpen])

  const clearUnattachedFile = useCallback(
    async (
      nextFile: File | null,
      operation: number,
      session: number,
    ): Promise<boolean> => {
      try {
        await flow.discardUnattachedFile(nextFile)
        if (
          operation !== fileOperationRef.current ||
          session !== draftSessionRef.current
        ) {
          return false
        }
        return true
      } catch {
        if (
          operation !== fileOperationRef.current ||
          session !== draftSessionRef.current
        ) {
          return false
        }
        setFileError(
          translate(
            'We could not remove the previous invoice attachment. Please try again.',
          ),
        )
        return false
      }
    },
    [flow.discardUnattachedFile, translate],
  )

  const handleFileAccepted = useCallback(
    async (file: File): Promise<void> => {
      const operation = ++fileOperationRef.current
      const session = draftSessionRef.current
      // A newer pick, or a new draft, owns this state from here on — including
      // turning the in-flight flag back off, which is why every write below is
      // guarded rather than just the early returns.
      const superseded = (): boolean =>
        operation !== fileOperationRef.current ||
        session !== draftSessionRef.current

      // Commit the file up front so the row — and its progress bar — is on
      // screen for the whole upload. The rollbacks below preserve the original
      // contract, which was to leave the field empty if the work fails.
      setInvoiceFile(file)
      setFileError(null)
      setIsAttachingFile(true)
      try {
        // Resolves false rather than throwing; it reports its own failure.
        const cleared = await clearUnattachedFile(file, operation, session)
        if (superseded()) return
        if (!cleared) {
          setInvoiceFile(null)
          return
        }
        // The preview renders the local File, so it opens without waiting.
        setIsPreviewOpen(true)
        await uploadInvoiceFile(file)
      } catch {
        if (superseded()) return
        setInvoiceFile(null)
        setIsPreviewOpen(false)
        setFileError(
          translate("This file couldn't be uploaded. Please try again."),
        )
      } finally {
        if (!superseded()) setIsAttachingFile(false)
      }
    },
    [clearUnattachedFile, uploadInvoiceFile, translate],
  )
  const handleFileRejected = useCallback(
    async (message: string): Promise<void> => {
      const operation = ++fileOperationRef.current
      const session = draftSessionRef.current
      // Clear the in-flight flag here rather than leaving it to the upload this
      // supersedes: that handler sees itself superseded and deliberately stops
      // touching state, so whoever takes over owns the flag. Leaving it set
      // would disable Next for the rest of the draft (see `canContinue`).
      setIsAttachingFile(false)
      if (!(await clearUnattachedFile(null, operation, session))) return
      setInvoiceFile(null)
      setFileError(message)
      setIsPreviewOpen(false)
    },
    [clearUnattachedFile],
  )
  const handleRemoveFile = useCallback(async (): Promise<void> => {
    const operation = ++fileOperationRef.current
    const session = draftSessionRef.current
    // Same as the rejection path: this supersedes any upload still running, and
    // that handler will not clear the flag on its way out.
    setIsAttachingFile(false)
    if (!(await clearUnattachedFile(null, operation, session))) return
    setInvoiceFile(null)
    setFileError(null)
    setIsPreviewOpen(false)
  }, [clearUnattachedFile])

  const handleConfirmAndPay = useCallback((): void => {
    if (flow.isSealed) {
      flow.openMfa()
      return
    }
    if (!selectedVendor || amount === undefined) return
    void flow.confirm(
      {
        vendorId: selectedVendor.id,
        amount,
        invoiceNumber,
        invoiceDate,
        dueDate,
        memo,
      },
      invoiceFile,
    )
  }, [
    amount,
    dueDate,
    flow,
    invoiceDate,
    invoiceFile,
    invoiceNumber,
    memo,
    selectedVendor,
  ])

  const handleClose = useCallback((): void => {
    if (flow.isInteractionLocked) return
    onClose()
  }, [flow.isInteractionLocked, onClose])

  const reviewMode =
    flow.phase === 'awaiting-authorization'
      ? 'sealed'
      : flow.phase === 'reconciling' || flow.phase === 'schedule-unknown'
        ? 'reconciling'
        : flow.phase

  // Live-validated, deliberately not armed-on-blur. For an upper bound there is
  // no mid-typing flicker to avoid: appending digits crosses the threshold
  // exactly once and stays over, so the message appears when the number really
  // is too large and clears the moment it isn't. (A minimum would need the
  // armed pattern; a maximum does not.)
  const amountOverLimit = amount !== undefined && amount > ACH_MAX_AMOUNT

  const amountError = amountOverLimit
    ? translate('Amount cannot exceed ${limit} per payment', {
        limit: formatAchMaxAmountLabel(locale),
      })
    : undefined

  // Validation gate (decision #1): a PAYABLE vendor is selected AND Invoice
  // Number is non-empty AND Amount is present and > 0. Stricter than the
  // prototype (which gates on vendor only) — the ticket AC wins. The optional
  // fields (both dates, memo) never gate.
  // An in-flight attachment also gates, which the optional-fields rule above
  // does not contradict: the invoice stays optional, but reaching Confirm
  // before its upload resolves makes `confirm` fire a second POST on the same
  // idempotency key for a file it cannot see the id of yet.
  const canContinue =
    selectedVendor !== null &&
    isPayableVendor(selectedVendor) &&
    invoiceNumber.trim() !== '' &&
    amount !== undefined &&
    amount > 0 &&
    !amountOverLimit &&
    !isAttachingFile

  /* `flow.error.error` is the API's `reason` — extend-api has already resolved
     it through its own message bundle for the request locale (`msg(...)` reads
     LocaleContextHolder, and messages_fr_CA.properties is a real file). Passing
     it back through `translate()` treats a finished sentence as a translation
     key: every server message that isn't coincidentally in en-US.json rendered
     as `"Invoi..." MISSING in this locale` instead of the actual error. Render
     it as-is. */
  const reviewErrorMessage =
    flow.error?.errorCode === 'PAYMENT_CONFIRM_RESPONSE_INVALID'
      ? translate('Something went wrong. Please try again.')
      : flow.error?.error
        ? flow.error.error
        : undefined

  return (
    <BaseFlyout
      className={cn('PayBillFlyout', className)}
      css={style}
      isOpen={isOpen}
      onClose={handleClose}
      showCloseButton={!flow.isInteractionLocked}
      aria-label={translate('Pay Bill')}
      secondaryFlyoutContent={
        invoiceFile === null
          ? undefined
          : {
              ariaLabel: translate('Invoice Preview'),
              closeButtonAriaLabel: translate('Close invoice preview'),
              title: translate('Invoice Preview'),
              subtitle: invoiceFile.name,
              content: <InvoicePreviewShelf file={invoiceFile} />,
              isPanelOpen: isPreviewOpen,
              onPanelOpenChange: setIsPreviewOpen,
            }
      }
    >
      <div css={[header, headerDividerFullBleed]}>
        <h2 css={flyoutTitle} id={FLYOUT_TITLE_ID}>
          {translate('Pay Bill')}
        </h2>
      </div>

      {step === 'details' ? (
        <div
          css={stepBody}
          ref={detailsRef}
          tabIndex={-1}
          role={'group'}
          aria-labelledby={FLYOUT_TITLE_ID}
        >
          <div css={fields}>
            {/* Three titled sections separated by hairline dividers, matching
                the prototype's FlyoutSection/FlyoutDivider grouping. */}
            <section css={section} aria-labelledby={VENDOR_SECTION_ID}>
              <h3 css={sectionTitle} id={VENDOR_SECTION_ID}>
                {translate('Select Vendor')}
              </h3>
              <div css={sectionFields}>
                <VendorSelect
                  vendors={vendors}
                  isLoading={vendorsQuery.isLoading}
                  selectedVendor={selectedVendor}
                  onSelect={setSelectedVendor}
                />
              </div>
            </section>

            <div css={sectionDivider} />

            <section css={section} aria-labelledby={ATTACHMENT_SECTION_ID}>
              {/* Title lives here, not inside InvoiceUploader, so all three
                  sections share one heading treatment. Fixed wording in both
                  states — the prototype swapped it to "Attach your Invoice"
                  while empty, but a heading that renames itself on upload
                  reads as a different section rather than the same one. */}
              <h3 css={sectionTitle} id={ATTACHMENT_SECTION_ID}>
                {translate('Invoice Attachment')}
              </h3>
              <div css={sectionFields}>
                <InvoiceUploader
                  file={invoiceFile}
                  error={fileError}
                  isUploading={isAttachingFile}
                  onFileAccepted={handleFileAccepted}
                  onFileRejected={handleFileRejected}
                  onRemove={handleRemoveFile}
                  onPreview={() => setIsPreviewOpen(true)}
                />
              </div>
            </section>

            <div css={sectionDivider} />

            <section css={section} aria-labelledby={DETAILS_SECTION_ID}>
              <h3 css={sectionTitle} id={DETAILS_SECTION_ID}>
                {translate('Invoice Details')}
              </h3>
              <div css={sectionFields}>
                <TextInput
                  label={translate('Invoice Number')}
                  requirement={'required'}
                  placeholder={translate('Enter invoice number')}
                  value={invoiceNumber}
                  onChange={(event) => setInvoiceNumber(event.target.value)}
                  testId={'pay-bill-invoice-number'}
                />

                <AmountInput
                  label={translate('Amount')}
                  requirement={'required'}
                  value={amount}
                  onChange={setAmount}
                  maxValue={AMOUNT_MAX_VALUE}
                  locale={locale}
                  placeholder={translate('Enter amount')}
                  // ACH is USD-only, so the currency is fixed and rendered as a
                  // static adornment rather than a selector.
                  currency={ExtendCurrency.Usd}
                  // Blocks Next (see `canContinue`) rather than clamping. The
                  // limit is interpolated so the message and the constant can
                  // never disagree.
                  errorText={amountError}
                  testId={'pay-bill-amount'}
                />

                <div css={datesRow}>
                  <DateInput
                    label={translate('Invoice Date')}
                    placeholder={translate('Enter invoice date')}
                    tooltip={
                      <TooltipIcon>
                        {translate('The date the vendor issued this invoice.')}
                      </TooltipIcon>
                    }
                    value={invoiceDate}
                    onChange={setInvoiceDate}
                    // Invoice Date is always in the past or today.
                    allowPastAndCurrentDates
                    // Downward, and pinned there: the calendar reads as
                    // belonging to the field it came from rather than covering
                    // the form above it. `pinPlacement` scrolls the field up to
                    // make the room; the field list carries extra trailing
                    // space for exactly that (`calendarClearance` in
                    // PayBillFlyout.style).
                    placement={'bottom-start'}
                    pinPlacement
                    testId={'pay-bill-invoice-date'}
                  />
                  <DateInput
                    label={translate('Due Date')}
                    placeholder={translate('Enter due date')}
                    tooltip={
                      <TooltipIcon>
                        {translate('When payment is due to the vendor.')}
                      </TooltipIcon>
                    }
                    value={dueDate}
                    onChange={setDueDate}
                    // Overdue invoices are routine in AP — a past due date must be
                    // selectable, so allow all dates.
                    allowAllDates
                    // Right-hand column: anchor the calendar to the field's right
                    // edge so it extends leftward and stays inside the flyout,
                    // instead of overflowing past its right edge.
                    placement={'bottom-end'}
                    pinPlacement
                    testId={'pay-bill-due-date'}
                  />
                </div>

                {/* A memo is prose, not a single value — a one-line input
                    scrolls what the user already typed out of sight. */}
                <TextAreaInput
                  css={memoField}
                  label={translate('Memo')}
                  placeholder={translate("What's this payment for?")}
                  value={memo}
                  onChange={(event) => setMemo(event.target.value)}
                  rows={3}
                  testId={'pay-bill-memo'}
                />
              </div>
            </section>
          </div>

          <div css={footer}>
            <div css={footerButtons}>
              <Button
                variant={'no-shell'}
                type={'button'}
                onClick={handleClose}
                data-testid={'pay-bill-cancel'}
              >
                {translate('Cancel')}
              </Button>
              <Button
                variant={'primary'}
                type={'button'}
                disabled={!canContinue}
                onClick={() => setStep('review')}
                data-testid={'pay-bill-next'}
                // Trailing chevron, matching the prototype's
                // `iconRight={<ChevronRightIcon />}`. `currentColor` because
                // SvgChevronRight defaults its fill to the CTA blue, which is
                // invisible on a primary (blue) button.
                iconRight={<SvgChevronRight fill={'currentColor'} />}
              >
                {translate('Next: Review and Pay')}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div
          css={stepBody}
          ref={reviewRef}
          tabIndex={-1}
          role={'group'}
          aria-labelledby={FLYOUT_TITLE_ID}
        >
          <ReviewStep
            organizationId={organizationId}
            vendor={selectedVendor}
            amount={amount}
            locale={locale}
            invoiceNumber={invoiceNumber}
            invoiceDate={invoiceDate}
            dueDate={dueDate}
            mode={reviewMode}
            errorMessage={reviewErrorMessage}
            onBack={() => setStep('details')}
            onConfirm={handleConfirmAndPay}
          />
        </div>
      )}
      {flow.details?.authorization && (
        <MfaStepUpModal
          open={flow.isMfaOpen}
          payment={flow.details.payment}
          authorization={flow.details.authorization}
          deliveryUpdate={flow.deliveryUpdate}
          onClose={flow.closeMfa}
          onResend={flow.resend}
          onRestart={flow.restart}
          onReconcileSchedule={flow.reconcileSchedule}
          onScheduled={(payment) => {
            flow.acceptScheduled(payment)
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: <strong>{translate('Payment scheduled')}</strong>,
              }),
            )
            onClose()
          }}
          onTerminalError={(error) => void flow.acceptTerminalError(error)}
        />
      )}
    </BaseFlyout>
  )
}

export default PayBillFlyout
